// oxlint-disable jsx-no-new-object-as-prop

import { EASE } from "@nnf3d/lib";
import { useStore } from "@tanstack/react-store";
import type { SpringOptions } from "motion/react";
import { animate, m, mix, useSpring, useTransform } from "motion/react";
import { useEffect } from "react";

import { useIsMobile } from "../lib/hooks/use-breakpoints";
import { framesFiredStore, loadStore } from "../lib/store";
import type { VoidComponent } from "../lib/types";
import { LoaderVideos } from "./loader-videos";

const OUTER_CIRCLE = {
	START: { CX: 0.5, CY: 0.5, R: 0.2862 },
	END: { CX: 0.5, CY: 0.5, R: 0.375 },
} as const;

const INNER_CIRCLE = {
	START: { CX: 0.5262, CY: 0.4528, R: 0.34 },
	END: { CX: 0.511, CY: 0.464, R: 0.25 },
} as const;

const springOptions = {
	visualDuration: 0.7,
	bounce: 0.3,
	restDelta: 0.00001,
} as const satisfies SpringOptions;

const outerCircleCxMixer = mix(OUTER_CIRCLE.START.CX, OUTER_CIRCLE.END.CX);
const outerCircleCyMixer = mix(OUTER_CIRCLE.START.CY, OUTER_CIRCLE.END.CY);
const outerCircleRMixer = mix(OUTER_CIRCLE.START.R, OUTER_CIRCLE.END.R);
const innerCircleCxMixer = mix(INNER_CIRCLE.START.CX, INNER_CIRCLE.END.CX);
const innerCircleCyMixer = mix(INNER_CIRCLE.START.CY, INNER_CIRCLE.END.CY);
const innerCircleRMixer = mix(INNER_CIRCLE.START.R, INNER_CIRCLE.END.R);

interface Props {
	isRevealComplete: boolean;
	progress: number;
}

const LoaderMask: VoidComponent<Props> = ({ isRevealComplete, progress }) => {
	const isMobile = useIsMobile();

	const { isStarted } = useStore(loadStore);
	const framesFired = useStore(framesFiredStore);

	const revealProgressSpring = useSpring(0, springOptions);
	const outerCircleCx = useSpring(OUTER_CIRCLE.START.CX, springOptions);
	const outerCircleCy = useSpring(OUTER_CIRCLE.START.CY, springOptions);
	const outerCircleR = useSpring(OUTER_CIRCLE.START.R, springOptions);
	const innerCircleCx = useSpring(INNER_CIRCLE.START.CX, springOptions);
	const innerCircleCy = useSpring(INNER_CIRCLE.START.CY, springOptions);
	const innerCircleR = useSpring(INNER_CIRCLE.START.R, springOptions);
	const maskWidth = useSpring(0, springOptions);
	const maskSize = useTransform(() => `${maskWidth.get()}%`);

	useEffect(() => {
		const REAL_FACTOR = 0.85;
		const FRAME_FACTOR = 1 - REAL_FACTOR;
		const newValue = isRevealComplete
			? 100
			: Math.round(progress * REAL_FACTOR + framesFired * 0.1 * FRAME_FACTOR);
		const pct = newValue / 100;
		if (pct > revealProgressSpring.get()) {
			revealProgressSpring.set(pct);
			outerCircleCx.set(outerCircleCxMixer(pct));
			outerCircleCy.set(outerCircleCyMixer(pct));
			outerCircleR.set(outerCircleRMixer(pct));
			innerCircleCx.set(innerCircleCxMixer(pct));
			innerCircleCy.set(innerCircleCyMixer(pct));
			innerCircleR.set(innerCircleRMixer(pct));
			maskWidth.set(maskWidth.get() === 99 || pct === 1 ? 100 : 99.9);
		}
	}, [
		revealProgressSpring,
		isRevealComplete,
		progress,
		framesFired,
		maskWidth,
		outerCircleCx,
		outerCircleCy,
		outerCircleR,
		innerCircleCx,
		innerCircleCy,
		innerCircleR,
	]);

	useEffect(() => {
		if (!isStarted) {
			return;
		}

		animate(1, 0, {
			duration: 0.7,
			ease: EASE.IN_EXPO,
			onUpdate(pct) {
				outerCircleCx.set(outerCircleCxMixer(pct));
				outerCircleCy.set(outerCircleCyMixer(pct));
				outerCircleR.set(outerCircleRMixer(pct));
				innerCircleCx.set(innerCircleCxMixer(pct));
				innerCircleCy.set(innerCircleCyMixer(pct));
				innerCircleR.set(innerCircleRMixer(pct));
			},
		});
	}, [
		isStarted,
		outerCircleCx,
		outerCircleCy,
		outerCircleR,
		innerCircleCx,
		innerCircleCy,
		innerCircleR,
		maskWidth,
	]);

	return (
		<>
			<svg className="sr-only" aria-hidden="true" id="logo-mask-svg">
				<mask id="logo-mask-outer" maskContentUnits="objectBoundingBox">
					{/* outer circle */}
					<m.circle
						style={{ cx: outerCircleCx, cy: outerCircleCy, r: outerCircleR }}
						fill="white"
					/>
				</mask>

				<mask id="logo-mask-inner" maskContentUnits="objectBoundingBox">
					{/* inner circle */}
					<m.circle
						style={{ cx: innerCircleCx, cy: innerCircleCy, r: innerCircleR }}
						fill="white"
					/>
				</mask>
			</svg>

			<m.div
				className="absolute overflow-hidden will-change-[mask-size,-webkit-mask-size]"
				initial={{ "--logo-mask-size": isMobile ? "60vmin" : "40vmin" }}
				exit={{
					"--logo-mask-size": isMobile ? "120vmin" : "100vmin",
					transition: { duration: 1, ease: EASE.IN_EXPO },
				}}
				style={{
					top: "calc(50dvh - var(--logo-mask-size) / 2)",
					left: "calc(50dvw - var(--logo-mask-size) / 2)",
					bottom: "calc(50dvh - var(--logo-mask-size) / 2)",
					right: "calc(50dvw - var(--logo-mask-size) / 2)",
					maskSize,
					WebkitMaskSize: maskSize,
					maskImage: "url(#logo-mask-outer), url(#logo-mask-inner)",
					WebkitMaskImage: "url(#logo-mask-outer), url(#logo-mask-inner)",
					maskComposite: "subtract",
					WebkitMaskComposite: "subtract",
					maskPosition: "center",
					WebkitMaskPosition: "center",
					maskRepeat: "no-repeat",
					WebkitMaskRepeat: "no-repeat",
				}}
			>
				<div
					className="absolute"
					style={{
						top: "calc(-40dvh + var(--logo-mask-size) / 2)",
						left: "calc(-40dvw + var(--logo-mask-size) / 2)",
						bottom: "calc(-40dvh + var(--logo-mask-size) / 2)",
						right: "calc(-40dvw + var(--logo-mask-size) / 2)",
					}}
				>
					<div className="absolute inset-0 blur-sm">
						<div className="absolute inset-0 bg-white" />

						<LoaderVideos inMask />
					</div>

					<div className="absolute inset-0">
						{isRevealComplete && (
							<m.div
								className="size-full bg-accent"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1, transition: { ...springOptions, type: "spring" } }}
								exit={{ opacity: 0, transition: { duration: 0.6, ease: EASE.IN_EXPO } }}
							/>
						)}
					</div>
				</div>
			</m.div>
		</>
	);
};

export { LoaderMask };
